import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "../../assets/scss/Settings/Settings.module.scss";
import dummyProfile from "../../assets/images/Profile.png";
import { BaseURL } from "../../lib/api";
import { getUserInfo } from "../../utils/user.util";
import { useGetUserByIdQuery } from "../../slices/admin/admin";

const BannerSettings = () => {
    const localUser = getUserInfo();
    const userId = localUser?._id;
    const navigate = useNavigate();

    const { data: apiData, refetch } = useGetUserByIdQuery(userId, { skip: !userId });

    const [appBannerFiles, setAppBannerFiles] = useState([]);
    const [appBannerPreviews, setAppBannerPreviews] = useState([]);
    const [appExistingBanners, setAppExistingBanners] = useState([]);

    const [bannerFiles, setBannerFiles] = useState([]);
    const [bannerPreviews, setBannerPreviews] = useState([]);
    const [existingBanners, setExistingBanners] = useState([]);

    useEffect(() => {
        if (apiData?.result?.length > 0) {
            const user = apiData.result[0];
            if (user.bannerImage) {
                // Handle cases where bannerImage might be a single string or an array (as string or parsed)
                let images = [];
                try {
                    images = typeof user.bannerImage === 'string' && user.bannerImage.startsWith('[')
                        ? JSON.parse(user.bannerImage)
                        : Array.isArray(user.bannerImage) ? user.bannerImage : [user.bannerImage];
                } catch (e) {
                    images = [user.bannerImage];
                }

                setAppExistingBanners(images.map(img => 
                    (typeof img === "string" && img.startsWith("http")) ? img : `${BaseURL}/${img}`
                ));
            }
        }
    }, [apiData]);

    const canAccessHomeAndBanner = ["siteadmin", "siteowner"].includes(localUser?.type);


    useEffect(() => {
        const fetchLandingBanners = async () => {
            try {
                const rawToken = localStorage.getItem("token");
                const token = rawToken?.replace(/^"|"$/g, "");
                const res = await axios.get(`${BaseURL}/dashboard/getLandingBanners`, {
                    headers: { authorization: token },
                });

                if (res?.data?.result?.landingPageImages) {
                    setExistingBanners(
                        res.data.result.landingPageImages.map((p) =>
                            (typeof p === "string" && p.startsWith("http")) ? p : `${BaseURL}/${p}`
                        )
                    );
                }
            } catch (error) {
                console.error("Failed to fetch banners", error);
            }
        };

        fetchLandingBanners();
    }, []);

    const handleBannerChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter((file) => {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(`${file.name} is larger than 2MB`);
                return false;
            }
            return true;
        });

        const previews = validFiles.map((file) => URL.createObjectURL(file));
        setAppBannerFiles((prev) => [...prev, ...validFiles]);
        setAppBannerPreviews((prev) => [...prev, ...previews]);
    };

    const handleRemoveAppBanner = (index, type) => {
        if (type === "existing") {
            setAppExistingBanners((prev) => prev.filter((_, i) => i !== index));
        } else {
            setAppBannerFiles((prev) => prev.filter((_, i) => i !== index));
            setAppBannerPreviews((prev) => prev.filter((_, i) => i !== index));
        }
    };

    const handleBannerChangeHome = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter((file) => {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(`${file.name} is larger than 2MB`);
                return false;
            }
            return true;
        });

        const previews = validFiles.map((file) => URL.createObjectURL(file));
        setBannerFiles((prev) => [...prev, ...validFiles]);
        setBannerPreviews((prev) => [...prev, ...previews]);
    };

    const handleRemoveBanner = (index, type) => {
        if (type === "existing") {
            setExistingBanners((prev) => prev.filter((_, i) => i !== index));
        } else {
            setBannerFiles((prev) => prev.filter((_, i) => i !== index));
            setBannerPreviews((prev) => prev.filter((_, i) => i !== index));
        }
    };

    const handleSaveAppBanner = async () => {
        try {
            const formData = new FormData();
            
            // Add existing images that were NOT removed
            appExistingBanners.forEach((url) => {
                const relativePath = url.replace(`${BaseURL}/`, "");
                formData.append("existingImages", relativePath);
            });

            // Add new files
            appBannerFiles.forEach((file) => formData.append("bannerImage", file));

            const rawToken = localStorage.getItem("token");
            const token = rawToken?.replace(/^"|"$/g, "");

            const response = await axios.put(`${BaseURL}/user/update/settings`, formData, {
                headers: {
                    authorization: token,
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success(response.data.message || "App banners updated successfully!");
            setAppBannerFiles([]);
            setAppBannerPreviews([]);
            refetch();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update app banners");
        }
    };

    const handleBannerSave = async () => {
        try {
            const formData = new FormData();

            existingBanners.forEach((url) => {
                const relativePath = url.replace(`${BaseURL}/`, "");
                formData.append("existingImages", relativePath);
            });

            bannerFiles.forEach((file) => formData.append("landingPageImages", file));

            const rawToken = localStorage.getItem("token");
            const token = rawToken?.replace(/^"|"$/g, "");

            const res = await axios.post(`${BaseURL}/dashboard/landingPage`, formData, {
                headers: {
                    authorization: token,
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success(res?.data?.message || "Landing page banners updated successfully!");
            setBannerFiles([]);
            setBannerPreviews([]);

            const updated = await axios.get(`${BaseURL}/dashboard/getLandingBanners`, {
                headers: { authorization: token },
            });
            if (updated?.data?.result?.landingPageImages) {
                setExistingBanners(updated.data.result.landingPageImages.map((p) => `${BaseURL}/${p}`));
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to update landing page banners");
        }
    };

    return (
        <div className={styles.settingsContainer}>
            <div className={styles.headerRow}>
                <h2 className={styles.header}>Banner Settings</h2>
            </div>

            <div className={styles.card}>
                <h2 className={styles.header}>App Banner Settings</h2>
                <div className={styles.imageContainerMulti}>
                    {appExistingBanners.length > 0 && (
                        <div className={styles.previewGrid}>
                            {appExistingBanners.map((src, index) => (
                                <div key={`app-existing-${index}`} className={styles.previewBox}>
                                    <img src={src} alt={`Existing App Banner ${index + 1}`} className={styles.previewImage} />
                                    <button type="button" className={styles.removeButton} onClick={() => handleRemoveAppBanner(index, "existing")}>✕</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {appBannerPreviews.length > 0 && (
                        <div className={styles.previewGrid}>
                            {appBannerPreviews.map((src, index) => (
                                <div key={`app-new-${index}`} className={styles.previewBox}>
                                    <img src={src} alt={`New App Banner ${index + 1}`} className={styles.previewImage} />
                                    <button type="button" className={styles.removeButton} onClick={() => handleRemoveAppBanner(index, "new")}>✕</button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{ marginTop: "20px" }}>
                        <label htmlFor="banner-upload" className={styles.changeImageButton}>Upload App Banner Images</label>
                        <input id="banner-upload" type="file" accept="image/png, image/jpeg" multiple onChange={handleBannerChange} style={{ display: "none" }} />
                        <p className={styles.imageInfo}>You can upload multiple app banner images (JPG or PNG).</p>
                    </div>
                </div>

                <div className={styles.footerButtons}>
                    <button className={styles.cancel} onClick={() => { setAppBannerFiles([]); setAppBannerPreviews([]); navigate('/dashboard'); }}>Cancel</button>
                    <button className={styles.save} onClick={handleSaveAppBanner}>Save</button>
                </div>
            </div>
            {canAccessHomeAndBanner && (

                <div className={styles.card}>
                    <h2 className={styles.header}>Home Banner Settings</h2>
                    <div className={styles.imageContainerMulti}>
                        {existingBanners.length > 0 && (
                            <div className={styles.previewGrid}>
                                {existingBanners.map((src, index) => (
                                    <div key={`existing-${index}`} className={styles.previewBox}>
                                        <img src={src} alt={`Existing Banner ${index + 1}`} className={styles.previewImage} />
                                        <button type="button" className={styles.removeButton} onClick={() => handleRemoveBanner(index, "existing")}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {bannerPreviews.length > 0 && (
                            <div className={styles.previewGrid}>
                                {bannerPreviews.map((src, index) => (
                                    <div key={`new-${index}`} className={styles.previewBox}>
                                        <img src={src} alt={`New Banner ${index + 1}`} className={styles.previewImage} />
                                        <button type="button" className={styles.removeButton} onClick={() => handleRemoveBanner(index, "new")}>✕</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ marginTop: "20px" }}>
                            <label htmlFor="home-banner-upload" className={styles.changeImageButton}>Upload Home Banner Images</label>
                            <input id="home-banner-upload" type="file" accept="image/png, image/jpeg" multiple onChange={handleBannerChangeHome} style={{ display: "none" }} />
                            <p className={styles.imageInfo}>You can upload multiple banner images (JPG or PNG).</p>
                        </div>
                    </div>

                    <div className={styles.footerButtons}>
                        <button type="button" className={styles.cancel} onClick={() => { setBannerFiles([]); setBannerPreviews([]); navigate('/dashboard'); }}>Cancel</button>
                        <button type="button" className={styles.save} onClick={handleBannerSave}>Save</button>
                    </div>
                </div>

            )}

        </div>
    );
};

export default BannerSettings;
